// Subscriber chit detail — slot info, payment history for this chit, statement download.

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { orpc } from '../lib/orpc.js';
import { current_language } from '../i18n/index.js';
import { format_inr } from '../lib/format.js';

interface Payment {
  payment_id: string;
  chit_id: string;
  amount_paise: number;
  method: string;
  reference: string | null;
  timestamp: string;
  due_month: string;
  note: string | null;
}

export function SubChitDetail(): ReactElement {
  const { t } = useTranslation();
  const lang = current_language();
  const { chit_id = '' } = useParams<{ chit_id: string }>();

  const dash_q = useQuery({
    queryKey: ['sub-dashboard'],
    queryFn: () => orpc.sub.dashboard({}),
  });
  const hist_q = useQuery({
    queryKey: ['sub-history', chit_id],
    queryFn: () => orpc.sub.payment_history({ chit_id, limit: 100 }),
    enabled: !!chit_id,
  });
  const [pdf_url, set_pdf_url] = useState<string | null>(null);
  const pdf_m = useMutation({
    mutationFn: () => orpc.sub.statement_pdf({ chit_id }),
    onSuccess: (r) => set_pdf_url(r.pdf_url),
  });

  const chit_card = (dash_q.data?.chits as Array<{ chit: { id: string; name_ta: string; name_en: string; pool_size: number; monthly_subscription_paise: number; duration_months: number }; slot: number; total_paid_paise: number; total_expected_paise: number; next_due: { id: string; due_month: string; expected_amount_paise: number; total_paid_paise: number; status: string } | null }> | undefined)?.find(
    (c) => c.chit.id === chit_id,
  );

  if (dash_q.isLoading) return <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>;
  if (!chit_card) {
    return (
      <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
        {t('sub.detail.not_found', 'Chit not found.')}
        <div style={{ marginTop: 8 }}>
          <Link to="/sub" className="btn ghost sm">{t('actions.back', 'Back')}</Link>
        </div>
      </div>
    );
  }

  const payments = (hist_q.data?.payments ?? []) as Payment[];

  return (
    <>
      <Link to="/sub" className="btn ghost sm" style={{ alignSelf: 'flex-start' }}>
        ← {t('actions.back', 'Back')}
      </Link>
      <div>
        <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')} style={{ fontSize: 20 }}>
          {lang === 'ta' ? chit_card.chit.name_ta : chit_card.chit.name_en}
        </div>
        <div className="h-sub">
          {t('sub.slot', 'Slot')} {chit_card.slot} · {chit_card.chit.pool_size} {t('sub.members', 'members')} · {chit_card.chit.duration_months} {t('sub.months', 'months')}
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <SummaryTile label={t('sub.detail.paid', 'Paid')} paise={chit_card.total_paid_paise} tone="good" />
        <SummaryTile label={t('sub.detail.expected', 'Total expected')} paise={chit_card.total_expected_paise} />
      </div>

      {/* Next due */}
      {chit_card.next_due && chit_card.next_due.status !== 'paid' && (
        <div style={{ background: 'var(--primary-soft)', color: 'var(--primary-ink)', borderRadius: 'var(--radius-lg)', padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.75 }}>
              {t('sub.next_due', 'Next due')} · {chit_card.next_due.due_month}
            </div>
            <div className="mono" style={{ fontSize: 18, marginTop: 2 }}>
              {format_inr(Math.max(0, chit_card.next_due.expected_amount_paise - chit_card.next_due.total_paid_paise))}
            </div>
          </div>
          <Link to={`/sub/pay/${chit_card.next_due.id}`} className="btn primary">
            {t('sub.pay_now', 'Pay now')}
          </Link>
        </div>
      )}

      {/* Statement */}
      <div className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{t('sub.detail.statement', 'Statement PDF')}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t('sub.detail.statement_hint', 'Full per-month payment record')}</div>
        </div>
        {!pdf_url && (
          <button type="button" onClick={() => pdf_m.mutate()} disabled={pdf_m.isPending} className="btn primary sm">
            {pdf_m.isPending ? '…' : t('sub.detail.generate', 'Generate')}
          </button>
        )}
        {pdf_url && (
          <a href={pdf_url} target="_blank" rel="noreferrer" className="btn sm" style={{ background: 'var(--good)', color: '#fff', borderColor: 'var(--good)' }}>
            {t('actions.view', 'Open')}
          </a>
        )}
      </div>

      {/* Payment history */}
      <div className="section-band">{t('sub.detail.history', 'Payment history')}</div>
      {hist_q.isLoading && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Loading…</div>}
      {!hist_q.isLoading && payments.length === 0 && (
        <div className="card" style={{ padding: 14, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
          {t('sub.detail.no_payments', 'No payments yet for this chit.')}
        </div>
      )}
      {payments.length > 0 && (
        <div className="card sub-list" style={{ padding: 0 }}>
          {payments.map((p) => (
            <div key={p.payment_id} className="row">
              <div>
                <div className="mono" style={{ fontSize: 13 }}>{format_inr(p.amount_paise)}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {p.due_month} · {p.method.toUpperCase()}{p.reference ? ` · ${p.reference}` : ''}
                </div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{new Date(p.timestamp).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function SummaryTile({
  label,
  paise,
  tone,
}: {
  label: string;
  paise: number;
  tone?: 'good';
}): ReactElement {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={tone === 'good' ? { color: 'var(--good)' } : undefined}>{format_inr(paise)}</div>
    </div>
  );
}
